/**
 * Messaging Service
 * Handles attorney-client messaging with access control
 *
 * Access control:
 * - MarkoTM (internal) attorneys: Can message clients directly
 * - External attorneys: Must use internal requests through MarkoTM
 */
import { apiClient } from '../../services/api';

/**
 * Get messages for current user (attorney or client)
 */
export async function getMessages(options = {}) {
  const { limit = 50, offset = 0 } = options;
  const params = new URLSearchParams({ limit, offset });
  return apiClient.get(`/messages?${params}`);
}

/**
 * Get messages awaiting client response
 */
export async function getPendingMessages() {
  return apiClient.get('/messages/pending');
}

/**
 * Get a single message by ID
 */
export async function getMessageById(id) {
  return apiClient.get(`/messages/${id}`);
}

/**
 * Get messages by thread
 */
export async function getMessageThread(threadId) {
  return apiClient.get(`/messages/thread/${threadId}`);
}

/**
 * Get messages for an application
 */
export async function getMessagesByApplication(applicationId) {
  return apiClient.get(`/messages/application/${applicationId}`);
}

/**
 * Create a message to client (MarkoTM attorneys only)
 * External attorneys will get an error - they should use createInternalRequest
 */
export async function createClientMessage(applicationId, messageData) {
  return apiClient.post('/messages', {
    applicationId,
    type: messageData.type || 'info_request',
    subject: messageData.subject,
    body: messageData.body,
    requestData: messageData.requestData,
    attachments: messageData.attachments,
    urgency: messageData.urgency || 'medium',
    responseRequired: messageData.responseRequired ?? true,
    senderName: messageData.senderName,
    threadId: messageData.threadId,
    agentTaskId: messageData.agentTaskId
  });
}

/**
 * Client responds to a message
 */
export async function respondToMessage(messageId, responseData) {
  return apiClient.post(`/messages/${messageId}/respond`, {
    items: responseData.items,
    attachments: responseData.attachments,
    message: responseData.message,
    clientName: responseData.clientName
  });
}

// =============================================================================
// Internal Requests (External attorneys only)
// =============================================================================

/**
 * Create an internal request to MarkoTM (external attorneys only)
 */
export async function createInternalRequest(applicationId, requestData) {
  return apiClient.post('/internal-requests', {
    applicationId,
    type: requestData.type || 'info_request',
    subject: requestData.subject,
    body: requestData.body,
    requestData: requestData.requestData,
    attachments: requestData.attachments,
    urgency: requestData.urgency || 'medium',
    senderName: requestData.senderName
  });
}

/**
 * Get internal requests queue (MarkoTM attorneyadmin only)
 */
export async function getInternalRequests(status = 'all') {
  const params = status !== 'all' ? `?status=${status}` : '';
  return apiClient.get(`/internal-requests${params}`);
}

/**
 * Get my internal requests (external attorneys - their own requests)
 */
export async function getMyInternalRequests() {
  return apiClient.get('/internal-requests/mine');
}

/**
 * Get forwarded responses (external attorneys only)
 */
export async function getForwardedResponses() {
  return apiClient.get('/internal-requests/forwarded');
}

/**
 * Get a single internal request
 */
export async function getInternalRequestById(id) {
  return apiClient.get(`/internal-requests/${id}`);
}

/**
 * Process internal request - contact client (MarkoTM attorneyadmin)
 */
export async function contactClientForRequest(requestId, messageData) {
  return apiClient.post(`/internal-requests/${requestId}/contact-client`, {
    type: messageData.type || 'info_request',
    subject: messageData.subject,
    body: messageData.body,
    requestData: messageData.requestData,
    attachments: messageData.attachments,
    urgency: messageData.urgency,
    senderName: messageData.senderName
  });
}

/**
 * Forward client response to external attorney (MarkoTM)
 */
export async function forwardResponseToExternal(requestId, forwardData) {
  return apiClient.post(`/internal-requests/${requestId}/forward`, {
    clientMessageId: forwardData.clientMessageId,
    summary: forwardData.summary,
    items: forwardData.items,
    attachments: forwardData.attachments,
    senderName: forwardData.senderName
  });
}

/**
 * Decline an internal request (MarkoTM attorneyadmin)
 */
export async function declineInternalRequest(requestId, reason) {
  return apiClient.post(`/internal-requests/${requestId}/decline`, {
    reason
  });
}

export default {
  // Messages
  getMessages,
  getPendingMessages,
  getMessageById,
  getMessageThread,
  getMessagesByApplication,
  createClientMessage,
  respondToMessage,

  // Internal requests
  createInternalRequest,
  getInternalRequests,
  getMyInternalRequests,
  getForwardedResponses,
  getInternalRequestById,
  contactClientForRequest,
  forwardResponseToExternal,
  declineInternalRequest
};
